import React, { useState } from 'react';
import { 
  Copy, 
  Check, 
  Key, 
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { useApiKeys } from '../contexts/ApiKeysContext';
import { useBilling } from '../contexts/BillingContext';
import PageHeader from './PageHeader';
import './ApiKeysPage.css';

interface ApiKeysPageProps {
  onNavigateToBilling?: () => void;
}

const ApiKeysPage: React.FC<ApiKeysPageProps> = ({ onNavigateToBilling }) => {
  const [copied, setCopied] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [newKeyTitle, setNewKeyTitle] = useState('');
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const { apiKeys, loading, createApiKey, updateApiKeyTitle, deleteApiKey } = useApiKeys();
  const { paymentMethods, paymentMethodsLoading } = useBilling();

  const createNewApiKey = async () => {
    // Check if user has payment method
    if (!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0)) {
      // Show error message - payment method required
      return;
    }
    
    const title = newKeyTitle.trim() || 'Untitled Key';
    try {
      const result = await createApiKey(title);
      if (result) {
        setNewKey(result.apiKey);
        setNewKeyTitle(''); // Reset title input
        setShowTitleInput(false); // Hide title input after creation
      }
    } catch (error) {
      // Error handling is done in createApiKey
      console.error('Failed to create API key:', error);
    }
  };

  const handleCreateKeyClick = () => {
    setShowTitleInput(true);
  };

  const revokeApiKey = async (keyId: string) => {
    await deleteApiKey(keyId);
  };

  const startEditing = (keyId: string, currentTitle: string) => {
    setEditingKey(keyId);
    setEditingTitle(currentTitle);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditingTitle('');
  };

  const saveTitle = async (keyId: string) => {
    if (editingTitle.trim()) {
      await updateApiKeyTitle(keyId, editingTitle.trim());
    }
    setEditingKey(null);
    setEditingTitle('');
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <div className="api-keys-page">
      <PageHeader
        icon={Key}
        title="API Keys"
        subtitle="Manage your API keys for authentication"
        actions={
          <button
            onClick={showTitleInput ? createNewApiKey : handleCreateKeyClick}
            disabled={loading || (!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0))}
            className="create-key-button"
            title={(!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0)) ? 'Payment method required' : ''}
          >
            <Plus className="button-icon" />
            <span>{loading ? 'Creating...' : showTitleInput ? 'Create Key' : 'Create Key'}</span>
          </button>
        }
      />

      <div className="console-card">
        {/* Payment Method Warning */}
        {!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0) && (
          <div className="payment-method-warning">
            <AlertCircle size={20} />
            <div className="warning-content">
              <strong>Payment Method Required</strong>
              <p>You need to add a payment method before creating API keys. Please add a payment method in your billing settings.</p>
              {onNavigateToBilling && (
                <button
                  onClick={onNavigateToBilling}
                  className="go-to-billing-button"
                >
                  <ExternalLink size={16} />
                  Go to Billing
                </button>
              )}
            </div>
          </div>
        )}

        {/* Title Input for New Key - Only show when creating */}
        {showTitleInput && (
          <div className="new-key-title-section">
            <label htmlFor="new-key-title">Key Title (optional)</label>
            <input
              id="new-key-title"
              type="text"
              placeholder="Enter a title for your API key..."
              value={newKeyTitle}
              onChange={(e) => setNewKeyTitle(e.target.value)}
              className="title-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  createNewApiKey();
                }
                if (e.key === 'Escape') {
                  setShowTitleInput(false);
                  setNewKeyTitle('');
                }
              }}
            />
            <div className="title-input-actions">
              <button
                onClick={createNewApiKey}
                disabled={loading || (!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0))}
                className="confirm-create-button"
                title={(!paymentMethodsLoading && (!paymentMethods || paymentMethods.length === 0)) ? 'Payment method required' : ''}
              >
                Create Key
              </button>
              <button
                onClick={() => {
                  setShowTitleInput(false);
                  setNewKeyTitle('');
                }}
                className="cancel-create-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {newKey && (
          <div className="new-key-section">
            <div className="new-key-header">
              <h3>New API Key Created</h3>
              <p>Copy this key now - you won't be able to see it again!</p>
            </div>
            <div className="api-key-display">
              {newKey}
              <button
                onClick={() => copyToClipboard(newKey)}
                className={`copy-button ${copied ? 'copied' : ''}`}
              >
                {copied ? (
                  <>
                    <Check className="button-icon" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="button-icon" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <button
              onClick={() => setNewKey(null)}
              className="dismiss-button"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="api-keys-list">
          {loading && apiKeys.length === 0 ? (
            <div className="loading-state">Loading API keys...</div>
          ) : apiKeys.length === 0 ? (
            <div className="empty-state">
              <Key className="empty-icon" />
              <p>No API keys found. Create your first key to get started.</p>
            </div>
          ) : (
            apiKeys.map((key) => (
              <div key={key.id} className="api-key-item">
                <div className="key-info">
                  <div className="key-title-section">
                    {editingKey === key.id ? (
                      <div className="title-edit">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          className="title-edit-input"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveTitle(key.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                        <div className="title-edit-buttons">
                          <button
                            onClick={() => saveTitle(key.id)}
                            className="save-button"
                            disabled={!editingTitle.trim()}
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="cancel-button"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="key-title-display">
                        <h4 className="key-title">{key.title || 'Untitled Key'}</h4>
                        <button
                          onClick={() => startEditing(key.id, key.title || 'Untitled Key')}
                          className="edit-title-button"
                          title="Edit title"
                        >
                          <Edit3 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="key-prefix">{key.key_prefix}</div>
                  {/* <div className="key-meta">
                    <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                    {key.last_used_at && (
                      <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                    )} */}
                  {/* </div> */}
                </div>
                <button
                  onClick={() => revokeApiKey(key.id)}
                  className="revoke-button"
                  disabled={loading}
                >
                  <Trash2 className="button-icon" />
                  <span>Revoke</span>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeysPage;
